import { buildSmartCartWidgetUsageToken } from '@pdqdev/pdq-shared/dist/utils/usage-token-utils';
import { SmartCartLayoutSectionId } from './smart-cart-form.model';

export enum SmartCartBlockType {
  TOP_BAR = 'top-bar',
  ANNOUNCEMENT_BAR = 'announcement-bar',
  GOALS_PROGRESS_BAR = 'goals-progress-bar',
  CART_ITEMS = 'cart-items',
  UPSELL_ITEMS = 'upsell-items',
  DISCOUNT_CODES = 'discount-codes',
  CHECKOUT_AREA = 'checkout-area',
  CART_SUBTOTAL = 'cart-subtotal',
  CONTENT = 'content',
  GIFT_UNLOCK = 'gift-unlock',
  DIGITAL_UPSELL = 'digital-upsell',
  ORDER_PROTECTION = 'order-protection',
}

export interface SmartCartBlockDefinition {
  type: SmartCartBlockType;
  label: string;
  allowedSections: SmartCartLayoutSectionId[];
  /** When true, only a single instance may exist across all sections. */
  unique: boolean;
  autoSection?: SmartCartLayoutSectionId;
  draggable: boolean;
  removable: boolean;
  toggleable: boolean;
}

export interface SmartCartBlock {
  /** Frontend-only synthetic id, stable across reorders. */
  id: string;
  type: SmartCartBlockType;
  enabled: boolean;
  /** Per-widget usage token, e.g. smart_cart_goals_progress_bar_<random>. Stable once generated. */
  usageToken: string;
  /** DB widget id — set when the block was loaded from an existing config. Absent for newly-added blocks. */
  widgetDbId?: number;
}

export interface SmartCartBlockOption {
  type: SmartCartBlockType;
  label: string;
  disabled: boolean;
}

const BODY_AND_FOOTER: SmartCartLayoutSectionId[] = ['body', 'footer'];

export const SMART_CART_BLOCK_DEFINITIONS: Record<SmartCartBlockType, SmartCartBlockDefinition> = {
  [SmartCartBlockType.TOP_BAR]: {
    type: SmartCartBlockType.TOP_BAR,
    label: 'Top Bar',
    allowedSections: ['header'],
    unique: true,
    autoSection: 'header',
    draggable: false,
    // Still not removable — every cart keeps its Top Bar — but it can be switched off so the header
    // can show only a Goals Progress Bar instead.
    removable: false,
    toggleable: true,
  },
  [SmartCartBlockType.ANNOUNCEMENT_BAR]: {
    type: SmartCartBlockType.ANNOUNCEMENT_BAR,
    label: 'Announcement Bar',
    allowedSections: BODY_AND_FOOTER,
    unique: false,
    draggable: true,
    removable: true,
    toggleable: true,
  },
  [SmartCartBlockType.GOALS_PROGRESS_BAR]: {
    type: SmartCartBlockType.GOALS_PROGRESS_BAR,
    label: 'Goals Progress Bar',
    allowedSections: ['header', 'body', 'footer'],
    unique: false,
    draggable: true,
    removable: true,
    toggleable: true,
  },
  [SmartCartBlockType.CART_ITEMS]: {
    type: SmartCartBlockType.CART_ITEMS,
    label: 'Cart Items',
    allowedSections: ['body'],
    unique: true,
    autoSection: 'body',
    draggable: true,
    removable: false,
    toggleable: false,
  },
  [SmartCartBlockType.UPSELL_ITEMS]: {
    type: SmartCartBlockType.UPSELL_ITEMS,
    label: 'Upsell items',
    allowedSections: BODY_AND_FOOTER,
    unique: false,
    draggable: true,
    removable: true,
    toggleable: true,
  },
  [SmartCartBlockType.DISCOUNT_CODES]: {
    type: SmartCartBlockType.DISCOUNT_CODES,
    label: 'Discount Codes',
    allowedSections: BODY_AND_FOOTER,
    unique: true,
    draggable: true,
    removable: true,
    toggleable: true,
  },
  [SmartCartBlockType.CART_SUBTOTAL]: {
    type: SmartCartBlockType.CART_SUBTOTAL,
    label: 'Cart Subtotal',
    allowedSections: ['footer'],
    unique: true,
    autoSection: 'footer',
    draggable: true,
    removable: false,
    toggleable: false,
  },
  [SmartCartBlockType.CHECKOUT_AREA]: {
    type: SmartCartBlockType.CHECKOUT_AREA,
    label: 'Checkout Area',
    allowedSections: ['footer'],
    unique: true,
    autoSection: 'footer',
    draggable: true,
    removable: false,
    toggleable: false,
  },
  [SmartCartBlockType.CONTENT]: {
    type: SmartCartBlockType.CONTENT,
    label: 'Content',
    allowedSections: BODY_AND_FOOTER,
    unique: false,
    draggable: true,
    removable: true,
    toggleable: true,
  },
  [SmartCartBlockType.GIFT_UNLOCK]: {
    type: SmartCartBlockType.GIFT_UNLOCK,
    label: 'Gift Unlock',
    allowedSections: BODY_AND_FOOTER,
    unique: false,
    draggable: true,
    removable: true,
    toggleable: true,
  },
  [SmartCartBlockType.DIGITAL_UPSELL]: {
    type: SmartCartBlockType.DIGITAL_UPSELL,
    label: 'Digital Upsell',
    allowedSections: BODY_AND_FOOTER,
    unique: false,
    draggable: true,
    removable: true,
    toggleable: true,
  },
  // Standalone-only widget (embedded into a merchant's own cart, not placed in the drawer).
  // Hidden from the normal drawer picker via HIDDEN_FOR_V1; the "Standalone widgets" editor seeds it directly.
  [SmartCartBlockType.ORDER_PROTECTION]: {
    type: SmartCartBlockType.ORDER_PROTECTION,
    label: 'Order Protection',
    allowedSections: BODY_AND_FOOTER,
    unique: true,
    draggable: false,
    removable: false,
    toggleable: true,
  },
};

// TODO: V1 only — these widgets aren't ready for the first ship. They stay in the registry
// (so existing saved configs still load) but the order-management picker filters them out.
// Delete this set when each widget is ready to be exposed.
export const HIDDEN_FOR_V1: ReadonlySet<SmartCartBlockType> = new Set([
  SmartCartBlockType.CONTENT,
  SmartCartBlockType.GIFT_UNLOCK,
  // Standalone-only — not offered in the normal drawer's widget picker.
  SmartCartBlockType.ORDER_PROTECTION,
]);

export const BLOCK_TYPE_TO_API_TYPE: Partial<Record<SmartCartBlockType, string>> = {
  [SmartCartBlockType.TOP_BAR]: 'top_bar',
  [SmartCartBlockType.ANNOUNCEMENT_BAR]: 'announcement_bar',
  [SmartCartBlockType.GOALS_PROGRESS_BAR]: 'goals_progress_bar',
  [SmartCartBlockType.CART_ITEMS]: 'cart_items',
  [SmartCartBlockType.UPSELL_ITEMS]: 'upsell_items',
  [SmartCartBlockType.DISCOUNT_CODES]: 'discount_codes',
  [SmartCartBlockType.CART_SUBTOTAL]: 'cart_subtotal',
  [SmartCartBlockType.CHECKOUT_AREA]: 'checkout_area',
  [SmartCartBlockType.DIGITAL_UPSELL]: 'digital_upsell',
  [SmartCartBlockType.ORDER_PROTECTION]: 'order_protection',
};

export const API_TYPE_TO_BLOCK_TYPE: Record<string, SmartCartBlockType> = Object.entries(BLOCK_TYPE_TO_API_TYPE).reduce(
  (acc, [blockType, apiType]) => {
    if (apiType) acc[apiType] = blockType as SmartCartBlockType;
    return acc;
  },
  {} as Record<string, SmartCartBlockType>,
);

// Format lives in pdq-shared so the admin and smart-cart backend can't drift.
export function buildWidgetUsageToken(type: SmartCartBlockType): string {
  const apiType = BLOCK_TYPE_TO_API_TYPE[type] ?? type.replace(/-/g, '_');
  return buildSmartCartWidgetUsageToken(apiType);
}
